import React, { FC } from 'react'

import { ButtonContainer, PageContent } from 'common'
import { InterSvgIcon } from 'components'
import { Button } from 'common/styles/layouts'

import { Text } from '@interco/inter-ui/components/Text'
import { Tag } from '@interco/inter-ui/components/Tag'
import { Timeline } from '@interco/inter-ui/components/Timeline'

import { TServiceDetailsViewProps } from '../types/ServiceDetails.types'
import {
  AboutContainer,
  ServiceActionsContainer,
  IconContainer,
  InfoContainer,
  ProtocolContainer,
  ServiceSubjectContainer,
  TagContainer,
  TitleContainer,
  TrackingContainer,
} from '../styles/ServiceDetails.styles'
import { TrackingItem } from './TrackingItem'

export const ServiceDetailsView: FC<TServiceDetailsViewProps> = ({
  aboutYourRequest,
  trackYourRequest,
  service,
  tag,
  trackingStatus,
  sendDocuments,
  isCanceled,
  customRef,
  hasScroll,
  isCompleted,
  supportResponse,
  isAwaitingProtocol,
  onClick,
  copyProtocol,
  onSendDocuments,
}) => {
  const { subject, openingDate, protocol, hasUpdate, areaResponse, additionalInfo, pendingReason } =
    service

  const isResponseEmpty = areaResponse !== undefined && areaResponse !== ''
  const isAditionalInfoEmpty = additionalInfo !== undefined && additionalInfo !== ''

  return (
    <PageContent orientation="VERTICAL" ref={customRef} data-testid="service-details-page-content">
      <AboutContainer data-testid="service-details-about">
        <TitleContainer>
          <Text variant="body-2" colorWeight={600} bold data-testid="service-details-about-title">
            {aboutYourRequest.title}
          </Text>
        </TitleContainer>
        <InfoContainer data-testid="service-details-info-subject">
          <Text variant="body-3" colorWeight={400} data-testid="service-details-subject-label">
            {aboutYourRequest.subject}
          </Text>
          <ServiceSubjectContainer
            variant="body-3"
            colorWeight={500}
            bold
            data-testid="service-details-subject"
          >
            {subject}
          </ServiceSubjectContainer>
        </InfoContainer>
        <InfoContainer data-testid="service-details-info-status">
          <Text variant="body-3" colorWeight={400} data-testid="service-details-status-label">
            {aboutYourRequest.status}
          </Text>
          <Tag
            theme={tag.theme}
            intensity="light"
            size="small"
            data-testid="service-details-status-tag"
          >
            {tag.status}
          </Tag>
        </InfoContainer>
        <InfoContainer data-testid="service-details-info-opening-date">
          <Text variant="body-3" colorWeight={400} data-testid="service-details-opening-date-label">
            {aboutYourRequest.openingDate}
          </Text>
          <Text variant="body-3" colorWeight={500} bold data-testid="service-details-opening-date">
            {openingDate}
          </Text>
        </InfoContainer>
        {protocol && (
          <InfoContainer data-testid="service-details-info-protocol">
            <Text variant="body-3" colorWeight={400} data-testid="service-details-protocol-label">
              {aboutYourRequest.protocolNumber}
            </Text>
            <ProtocolContainer onClick={copyProtocol} data-testid="service-details-protocol-button">
              <Text variant="body-3" colorWeight={500} bold data-testid="service-details-protocol">
                {protocol}
              </Text>
              <IconContainer>
                <InterSvgIcon
                  iconRef={aboutYourRequest.icon}
                  height={16}
                  width={16}
                  color="var(--orange500)"
                />
              </IconContainer>
            </ProtocolContainer>
          </InfoContainer>
        )}
      </AboutContainer>
      {!isAwaitingProtocol && trackingStatus && (
        <TrackingContainer data-testid="service-details-tracking">
          <TagContainer data-testid="service-details-tracking-header">
            <Text variant="body-2" colorWeight={600} as="p" bold>
              {trackYourRequest.title}
            </Text>

            {hasUpdate && (
              <Tag theme="orange" intensity="dark" size="small">
                {trackYourRequest.updatedTag}
              </Tag>
            )}
          </TagContainer>
          <Timeline data-testid="service-details-timeline">
            <TrackingItem
              title={trackYourRequest.status.open.title}
              type={trackingStatus.open.status}
              content={trackingStatus.open}
              icon={trackingStatus.open.icon}
              color={trackingStatus.open.color}
            />
            <TrackingItem
              title={trackYourRequest.status.analysis.title}
              type={trackingStatus.waiting.status}
              content={trackingStatus.waiting}
              hasActionButton={sendDocuments}
              buttonLabel={trackYourRequest.status.analysis.sendDocumentsButton.title}
              pendingReason={pendingReason}
              onClick={onSendDocuments}
              icon={trackingStatus.waiting.icon}
              color={trackingStatus.waiting.color}
            />
            <TrackingItem
              title={
                isCanceled
                  ? trackYourRequest.status.completed.canceledTitle
                  : trackYourRequest.status.completed.title
              }
              type={trackingStatus.completed.status}
              content={trackingStatus.completed}
              hasVerticalBar={false}
              icon={trackingStatus.completed.icon}
              color={trackingStatus.completed.color}
            >
              <>
                {isCompleted && (isResponseEmpty || isAditionalInfoEmpty) && (
                  <ButtonContainer $hasScroll={hasScroll} $notFixed>
                    <ServiceActionsContainer data-testid="service-details-check-results">
                      <Button
                        onClick={onClick}
                        variant="primary"
                        data-testid="service-details-check-results-button"
                      >
                        {supportResponse.buttonLabel}
                      </Button>
                    </ServiceActionsContainer>
                  </ButtonContainer>
                )}
              </>
            </TrackingItem>
          </Timeline>
        </TrackingContainer>
      )}
    </PageContent>
  )
}
